import React, { useEffect, useState } from 'react';
import { bookingBrandFontFamilyVar, teboxDesignSystem } from '../../styles/tebox-booking';
import type { BookingBrandColors } from '../../utils/booking-brand-colors';
import type { BookingCustomer } from '../../services/customer-auth.service';

export type BookingAccountMenuProps = {
  authenticatedCustomer: BookingCustomer | null;
  brandColors: BookingBrandColors;
  isMobile: boolean;
  onSignIn: () => void;
  onLogout: () => void | Promise<void>;
  showDashboard?: boolean;
  onOpenDashboard?: () => void;
  onCloseDashboard?: () => void;
  /** Label when dashboard is open (e.g. "Back to booking" / "Back to shop"). */
  dashboardBackLabel?: string;
  dashboardNavLabel?: string;
};

export function BookingAccountMenu({
  authenticatedCustomer,
  brandColors,
  isMobile,
  onSignIn,
  onLogout,
  showDashboard = false,
  onOpenDashboard,
  onCloseDashboard,
  dashboardBackLabel = 'Back to booking',
  dashboardNavLabel = 'Dashboard',
}: BookingAccountMenuProps) {
  const [showAccountMenu, setShowAccountMenu] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        showAccountMenu &&
        !target.closest('.account-menu-container') &&
        !target.closest('.account-menu-trigger')
      ) {
        setShowAccountMenu(false);
      }
    };

    if (showAccountMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAccountMenu]);

  if (!authenticatedCustomer) {
    return (
      <button
        type="button"
        onClick={onSignIn}
        className="rounded-[6px] transition-opacity hover:opacity-90"
        style={{
          fontFamily: bookingBrandFontFamilyVar,
          fontWeight: 500,
          fontSize: isMobile ? '13px' : '14px',
          padding: isMobile ? '8px 14px' : '10px 18px',
          backgroundColor: brandColors.buttonColor,
          color: brandColors.primaryTextColor,
          border: `1px solid ${brandColors.buttonColor}`,
          cursor: 'pointer',
          flexShrink: 0,
        }}
        aria-label="Sign in"
      >
        Sign in
      </button>
    );
  }

  const initial = (
    authenticatedCustomer.firstName?.trim().charAt(0) ||
    authenticatedCustomer.email?.charAt(0) ||
    '?'
  ).toUpperCase();

  return (
    <>
      <button
        type="button"
        className="account-menu-trigger flex items-center justify-center rounded-full border border-tebox-border bg-tebox-highlight hover:bg-tebox-hover transition-colors"
        onClick={() => setShowAccountMenu((open) => !open)}
        aria-label="Account menu"
        aria-expanded={showAccountMenu}
        style={{
          width: isMobile ? 36 : 40,
          height: isMobile ? 36 : 40,
          padding: 0,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontFamily: bookingBrandFontFamilyVar,
            fontWeight: 500,
            fontSize: isMobile ? 14 : 15,
            color: brandColors.textColor,
            lineHeight: 1,
          }}
        >
          {initial}
        </span>
      </button>

      {showAccountMenu ? (
        <div
          className="account-menu-container absolute top-full right-0 mt-2 bg-white border border-tebox-border rounded-[6px] shadow-lg z-50"
          style={{
            minWidth: isMobile ? '200px' : '240px',
            fontFamily: bookingBrandFontFamilyVar,
          }}
        >
          <div className="py-2">
            <div className="px-4 py-3 border-b border-tebox-border">
              <p
                style={{
                  fontFamily: bookingBrandFontFamilyVar,
                  fontWeight: 500,
                  fontSize: teboxDesignSystem.typography.body.fontSize,
                  color: brandColors.textColor,
                  margin: 0,
                }}
              >
                {`Hello, ${authenticatedCustomer.firstName}`}
              </p>
            </div>

            {showDashboard && onCloseDashboard ? (
              <button
                type="button"
                onClick={() => {
                  onCloseDashboard();
                  setShowAccountMenu(false);
                }}
                className="w-full text-left px-4 py-3 hover:bg-tebox-hover transition-colors"
                style={menuItemStyle(brandColors.textColor)}
              >
                {dashboardBackLabel}
              </button>
            ) : onOpenDashboard ? (
              <button
                type="button"
                onClick={() => {
                  onOpenDashboard();
                  setShowAccountMenu(false);
                }}
                className="w-full text-left px-4 py-3 hover:bg-tebox-hover transition-colors"
                style={menuItemStyle(brandColors.textColor)}
              >
                {dashboardNavLabel}
              </button>
            ) : null}

            <button
              type="button"
              onClick={async () => {
                await onLogout();
                setShowAccountMenu(false);
              }}
              className="w-full text-left px-4 py-3 hover:bg-tebox-hover transition-colors border-t border-tebox-border"
              style={menuItemStyle(brandColors.secondaryColor)}
            >
              Sign Out
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function menuItemStyle(color: string): React.CSSProperties {
  return {
    fontFamily: bookingBrandFontFamilyVar,
    fontWeight: 500,
    fontSize: teboxDesignSystem.typography.body.fontSize,
    color,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
  };
}

export default BookingAccountMenu;
